/**
 * Network — PeerJS-based multiplayer transport.
 *
 * Generalized port of the original `network.js` from the AR balloon game:
 *   https://github.com/cristi-boboc/ar-game/blob/claude/ar-balloon-game-FM7j2/js/network.js
 *
 * Differences from the original:
 *   - The peer-id prefix is taken from the constructor (driven by the game's
 *     manifest id) instead of being hardcoded to `arballoonpop-`.
 *   - Adds `kick(playerId)` for host moderation.
 *   - Adds a `gameVersion` field on the join handshake so incompatible clients
 *     can be rejected before they corrupt state.
 *   - All public callbacks and method shapes (`hostGame`, `joinGame`,
 *     `broadcast`, `sendTo`, `sendToHost`, `onPlayerJoined`, …) are preserved
 *     so the existing balloon game can drop this in unchanged.
 *
 * Higher-level concerns (snapshots, fixed-timestep ticks, latency
 * measurement, reconnection) live in sibling files: Sync.js, Tick.js,
 * Latency.js, Reconnect.js.
 */
export class Network {
  /**
   * @param {object} [opts]
   * @param {string} [opts.peerPrefix='platform'] - Prefix for the host peer ID.
   *   Use the manifest `id` so two games on the same PeerJS broker can't collide.
   * @param {string} [opts.gameVersion='0.0.0'] - Sent on the join handshake.
   * @param {object} [opts.peerOptions] - Forwarded to `new Peer(...)`.
   *   Lets callers pass a custom signaling host / TURN config without forking.
   */
  constructor(opts = {}) {
    this.peerPrefix = opts.peerPrefix || 'platform';
    this.gameVersion = opts.gameVersion || '0.0.0';
    this.peerOptions = Object.assign({ debug: 0 }, opts.peerOptions || {});

    this.peer = null;
    /** @type {Map<string, {connection: any, playerName: string, playerId: string}>} */
    this.connections = new Map();
    this.isHost = false;
    this.roomCode = '';
    this.playerId = '';
    this.playerName = '';
    this.hostConnection = null;

    // Public callback hooks (set by Sync / GameContainer / Lobby).
    this.onPlayerJoined = null;
    this.onPlayerLeft = null;
    this.onMessage = null;
    this.onConnected = null;
    this.onDisconnected = null;
    this.onError = null;
  }

  _generateRoomCode() {
    return String(Math.floor(10000 + Math.random() * 90000));
  }

  _generatePlayerId() {
    return 'p_' + Math.random().toString(36).substring(2, 9);
  }

  _peerIdFromCode(code) {
    return this.peerPrefix + '-' + code;
  }

  /**
   * Host a new game. Resolves with the 5-digit room code.
   * @param {string} playerName
   * @returns {Promise<string>}
   */
  async hostGame(playerName) {
    this.isHost = true;
    this.playerName = playerName;
    this.playerId = 'host';
    this.roomCode = this._generateRoomCode();

    const peerId = this._peerIdFromCode(this.roomCode);

    return new Promise((resolve, reject) => {
      this.peer = new Peer(peerId, this.peerOptions);

      this.peer.on('open', () => {
        this.peer.on('connection', (conn) => this._handleIncoming(conn));
        resolve(this.roomCode);
      });

      this.peer.on('error', (err) => {
        if (err.type === 'unavailable-id') {
          // Room code collision — regenerate.
          this.peer.destroy();
          this.roomCode = this._generateRoomCode();
          this.hostGame(playerName).then(resolve).catch(reject);
        } else {
          if (this.onError) this.onError(err.message || String(err));
          reject(err);
        }
      });
    });
  }

  /**
   * Join an existing game by room code. Resolves when connected.
   * @param {string} roomCode
   * @param {string} playerName
   * @returns {Promise<void>}
   */
  async joinGame(roomCode, playerName) {
    this.isHost = false;
    this.playerName = playerName;
    this.playerId = this._generatePlayerId();
    this.roomCode = roomCode;

    const hostPeerId = this._peerIdFromCode(roomCode);

    return new Promise((resolve, reject) => {
      this.peer = new Peer(this.playerId, this.peerOptions);

      let resolved = false;
      let timeoutId = null;
      const settle = (fn, arg) => {
        if (resolved) return;
        resolved = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        fn(arg);
      };

      this.peer.on('open', () => {
        const conn = this.peer.connect(hostPeerId, {
          reliable: true,
          metadata: {
            playerName: this.playerName,
            playerId: this.playerId,
            gameVersion: this.gameVersion
          }
        });

        conn.on('open', () => {
          this.hostConnection = conn;

          conn.on('data', (data) => {
            if (this.onMessage) this.onMessage(data);
          });

          conn.on('close', () => {
            if (this.onDisconnected) this.onDisconnected();
          });

          // Announce ourselves to the host.
          conn.send({
            type: 'player-join',
            playerId: this.playerId,
            playerName: this.playerName,
            gameVersion: this.gameVersion
          });

          if (this.onConnected) this.onConnected();
          settle(resolve);
        });

        conn.on('error', (err) => settle(reject, err));
      });

      this.peer.on('error', (err) => {
        if (this.onError) this.onError(err.message || String(err));
        settle(reject, err);
      });

      // Timeout after 12 seconds. Cleared on success/failure via `settle`.
      timeoutId = setTimeout(() => {
        settle(reject, new Error('Connection timed out. Check the room code and try again.'));
      }, 12000);
    });
  }

  _handleIncoming(conn) {
    conn.on('open', () => {
      conn.on('data', (data) => {
        if (data && data.type === 'player-join') {
          // Reject incompatible client versions early.
          if (data.gameVersion && data.gameVersion !== this.gameVersion) {
            try {
              conn.send({ type: 'kick', reason: 'version-mismatch', expected: this.gameVersion });
            } catch (_) { /* ignore */ }
            try { conn.close(); } catch (_) { /* ignore */ }
            return;
          }

          this.connections.set(data.playerId, {
            connection: conn,
            playerName: data.playerName,
            playerId: data.playerId
          });
          if (this.onPlayerJoined) {
            this.onPlayerJoined(data.playerId, data.playerName);
          }
        } else {
          if (this.onMessage) this.onMessage(data, conn);
        }
      });

      conn.on('close', () => {
        for (const [id, info] of this.connections) {
          if (info.connection === conn) {
            this.connections.delete(id);
            if (this.onPlayerLeft) this.onPlayerLeft(id);
            break;
          }
        }
      });
    });
  }

  /** Client → host. */
  sendToHost(data) {
    if (this.hostConnection && this.hostConnection.open) {
      this.hostConnection.send(data);
    }
  }

  /** Host → all clients. */
  broadcast(data) {
    for (const [, info] of this.connections) {
      if (info.connection.open) {
        info.connection.send(data);
      }
    }
  }

  /** Host → one client. */
  sendTo(playerId, data) {
    const info = this.connections.get(playerId);
    if (info && info.connection.open) {
      info.connection.send(data);
    }
  }

  /** Host removes a client. */
  kick(playerId, reason) {
    const info = this.connections.get(playerId);
    if (!info) return;
    try {
      info.connection.send({ type: 'kick', reason: reason || 'kicked' });
    } catch (_) { /* ignore */ }
    try {
      info.connection.close();
    } catch (_) { /* ignore */ }
    this.connections.delete(playerId);
    if (this.onPlayerLeft) this.onPlayerLeft(playerId);
  }

  /** Includes the host itself when running as host. */
  getPlayerCount() {
    return this.connections.size + (this.isHost ? 1 : 0);
  }

  /** All player IDs currently in the room (host first if hosting). */
  getPlayerIds() {
    const ids = [];
    if (this.isHost) ids.push('host');
    for (const id of this.connections.keys()) ids.push(id);
    return ids;
  }

  destroy() {
    if (this.peer) {
      try { this.peer.destroy(); } catch (_) { /* ignore */ }
      this.peer = null;
    }
    this.connections.clear();
    this.hostConnection = null;
  }
}
